import React, { useEffect, useState } from "react";
import { useCompanies } from "@/context/CompanyContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, fmtMoney, formatApiError, DOC_TYPE_META } from "@/lib/api";
import { TablePagination } from "@/components/TablePagination";
import { PageHeader, Content, StatusBadge, EmptyState } from "@/components/PageChrome";
import { Plus, Download, Pencil, Trash2, Search, FileText, Building2 } from "lucide-react";
import { toast } from "sonner";

const SECTIONS = {
  invoices: {
    overline: "Sales", title: "Invoices",
    description: "Proforma, Sales, Timesheet, Recurring, Progress, Commercial & Debit Notes.",
    types: ["proforma", "sales", "timesheet", "recurring", "progress", "commercial", "debit_note"],
    defaultNew: "sales",
  },
  receipts: {
    overline: "Cash", title: "Receipts",
    description: "Payment receipts issued to clients & internal expense receipts.",
    types: ["payment_receipt", "expense_receipt"],
    defaultNew: "payment_receipt",
  },
  "purchase-orders": {
    overline: "Procurement", title: "Purchase Orders",
    description: "POs issued to vendors and suppliers.",
    types: ["purchase_order"],
    defaultNew: "purchase_order",
  },
  notes: {
    overline: "Adjustments", title: "Credit / Debit Notes",
    description: "Refunds via credit notes, and additional charges via debit notes.",
    types: ["credit_note", "debit_note"],
    defaultNew: "credit_note",
  },
};

const STATUSES = ["all", "draft", "sent", "paid", "unpaid", "overdue", "cancelled"];

export default function DocumentsList() {
  const location = useLocation();
  const section = (location.pathname.split("/")[2] || "invoices");
  const s = SECTIONS[section] || SECTIONS.invoices;
  const nav = useNavigate();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const { scopeParams, scopeLabel, scopeIsAll, scopeIds = [], scopeVersion, version } = useCompanies();
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [debounced, setDebounced] = useState("");

  // The section spans several doc types and the type/status/search filters are
  // applied by the API, so searching covers every document rather than just the
  // most recently fetched batch. `scopeParams` carries the multi-company
  // selection (all_companies, or a comma-separated company_ids).
  const buildParams = (search, type, status, skip, size) => {
    const types = type !== "all" ? [type] : s.types;
    const params = { doc_types: types.join(","), skip: skip || 0, limit: size, ...scopeParams };
    if (status !== "all") params.status = status;
    if (search) params.q = search;
    return params;
  };

  const load = async (search, type, status, skip, size) => {
    setLoading(true);
    try {
      const params = buildParams(search, type, status, skip, size);
      const [{ data }, count] = await Promise.all([
        api.get("/documents", { params }),
        api.get("/documents/count", { params: buildParams(search, type, status, 0, 0) }),
      ]);
      setItems(Array.isArray(data) ? data : []);
      setTotal(count?.data?.count ?? (Array.isArray(data) ? data.length : 0));
    } catch (e) {
      toast.error(formatApiError(e));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // Debounce the search box; reset to the first page when the section or the
  // company scope changes, so a wider scope cannot land on an empty page 5.
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(handle);
  }, [q]);

  useEffect(() => { setPage(0); }, [section, version, scopeVersion]);

  useEffect(() => {
    load(debounced, typeFilter, statusFilter, page * pageSize, pageSize);
    // eslint-disable-next-line
  }, [debounced, typeFilter, statusFilter, page, pageSize, section, version, scopeVersion]);

  const changeFilter = (setter) => (e) => { setter(e.target.value); setPage(0); };
  const changePageSize = (n) => { setPageSize(n); setPage(0); };
  const filtered = items;
  const filtering = Boolean(debounced) || typeFilter !== "all" || statusFilter !== "all";
  // The issuer column is only noise when every row comes from one company, so
  // it appears exactly when the scope spans more than one.
  const showCompanyColumn = scopeIsAll || scopeIds.length > 1;

  const downloadPdf = async (id, number) => {
    try {
      const res = await api.get(`/documents/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${number || "document"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this document permanently?")) return;
    try {
      await api.delete(`/documents/${id}`);
      toast.success("Deleted");
      await load(debounced, typeFilter, statusFilter, page * pageSize, pageSize);
    }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <PageHeader
        overline={s.overline}
        title={s.title}
        description={s.description}
        testid={`page-${section}`}
        actions={
          <div className="relative group">
            <Link to={`/app/documents/new/${s.defaultNew}`} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="btn-new-document">
              <Plus className="w-4 h-4" /> New {DOC_TYPE_META[s.defaultNew]?.short || "Document"}
            </Link>
            {s.types.length > 1 && (
              <div className="absolute right-0 top-full mt-1 hidden group-hover:block bg-white border border-[var(--border)] rounded-md shadow-lg min-w-[220px] z-20">
                {s.types.map((t) => (
                  <Link key={t} to={`/app/documents/new/${t}`} className="block px-4 py-2 text-sm hover:bg-gray-50" data-testid={`new-${t}`}>
                    {DOC_TYPE_META[t]?.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        }
      />
      <Content>
        {/*
          States which companies the rows below belong to. Without this the
          table looks identical whether it is showing one company or ten, and a
          wider scope would silently pull in documents from companies the user
          had mentally excluded.
        */}
        <div
          className="mb-4 flex items-center gap-2 text-xs text-gray-500"
          data-testid="docs-scope"
        >
          <Building2 className="w-3.5 h-3.5 shrink-0" />
          <span>
            Showing <span className="font-medium text-gray-700">{scopeLabel}</span>
            {scopeIsAll ? " — every company on this account" : ""}
          </span>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search number or client…"
              className="h-10 pl-9 pr-3 border border-[var(--border)] rounded-md bg-white text-sm w-72"
              data-testid="search-input"
            />
          </div>
          {s.types.length > 1 && (
            <select value={typeFilter} onChange={changeFilter(setTypeFilter)}
              className="h-10 px-3 border border-[var(--border)] rounded-md bg-white text-sm"
              data-testid="type-filter">
              <option value="all">All types</option>
              {s.types.map((t) => <option key={t} value={t}>{DOC_TYPE_META[t]?.label}</option>)}
            </select>
          )}
          <select value={statusFilter} onChange={changeFilter(setStatusFilter)}
            className="h-10 px-3 border border-[var(--border)] rounded-md bg-white text-sm"
            data-testid="status-filter">
            {STATUSES.map((st) => <option key={st} value={st}>{st === "all" ? "All statuses" : st}</option>)}
          </select>
          <div className="ml-auto text-xs text-gray-500 num" data-testid="docs-count">{total} record(s)</div>
        </div>

        {loading && items.length === 0 ? (
          <div className="card-flat p-10 text-sm text-gray-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            title={filtering ? "Nothing matches these filters" : `No ${s.title.toLowerCase()} yet`}
            description={
              filtering
                ? "Try a different search term, type or status."
                : "Create your first one and pick from 5 template designs. Every document is stored as a PDF for your records."
            }
            action={!filtering && (
              <Link to={`/app/documents/new/${s.defaultNew}`} className="btn-primary inline-flex items-center gap-2">
                <FileText className="w-4 h-4" /> Create {DOC_TYPE_META[s.defaultNew]?.short}
              </Link>
            )}
            testid="empty-docs"
          />
        ) : (
          <div className="card-flat overflow-hidden">
            <div className="overflow-x-auto">
            <table className={`w-full text-sm ${showCompanyColumn ? "min-w-[880px]" : "min-w-[720px]"}`}>
              <thead className="text-left text-[11px] text-gray-500 uppercase tracking-wider">
                <tr className="border-b border-[var(--border)]">
                  <th className="px-5 py-3 font-medium">Number</th>
                  <th className="px-5 py-3 font-medium">Type</th>
                  {showCompanyColumn && <th className="px-5 py-3 font-medium">Company</th>}
                  <th className="px-5 py-3 font-medium">Client</th>
                  <th className="px-5 py-3 font-medium">Issued</th>
                  <th className="px-5 py-3 font-medium text-right">Total</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody data-testid="docs-table">
                {filtered.map((d) => (
                  <tr key={d.id} className="border-b border-[var(--border)] hover:bg-gray-50">
                    <td className="px-5 py-3 font-mono text-[13px]">
                      <Link to={`/app/documents/${d.id}`} className="hover:underline">{d.number}</Link>
                    </td>
<td className="px-5 py-3 text-gray-600">{DOC_TYPE_META[d.doc_type]?.short || d.doc_type}</td>
                      {showCompanyColumn && (
                        <td className="px-5 py-3 text-gray-600" data-testid={`doc-company-${d.id}`}>
                          {d.company_name || <span className="text-gray-400">No profile</span>}
                        </td>
                      )}
                      <td className="px-5 py-3">{d.client_snapshot?.name || "-"}</td>
                    <td className="px-5 py-3 text-gray-600 font-mono text-[12px]">{d.issue_date}</td>
                    <td className="px-5 py-3 text-right num">{fmtMoney(d.total, d.currency)}</td>
                    <td className="px-5 py-3"><StatusBadge status={d.status} /></td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <button className="p-2 hover:bg-gray-100 rounded" title="PDF" onClick={() => downloadPdf(d.id, d.number)} data-testid={`pdf-${d.id}`}>
                        <Download className="w-4 h-4" />
                      </button>
                      <button className="p-2 hover:bg-gray-100 rounded" title="Edit" onClick={() => nav(`/app/documents/${d.id}`)} data-testid={`edit-${d.id}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button className="p-2 hover:bg-gray-100 rounded text-red-600" title="Delete" onClick={() => del(d.id)} data-testid={`delete-${d.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <div className="px-5 pb-4">
              <TablePagination
                page={page}
                pageCount={Math.ceil(total / pageSize)}
                total={total}
                pageSize={pageSize}
                onPage={setPage}
                onPageSize={changePageSize}
                idPrefix="docs"
                noun="document"
              />
            </div>
          </div>
        )}
      </Content>
    </>
  );
}
